// app/guides/by-goal.tsx — בחירת קורס לפי מטרה | Kursi
import Head from 'expo-router/head';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
} from 'react-native';
import { useRouter, Link } from 'expo-router';
import { theme } from '@/constants/theme';

const SITE_URL = 'https://kursiaii.com';

const breadcrumbSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'קורסים', item: `${SITE_URL}/courses` },
    { '@type': 'ListItem', position: 3, name: 'בחירת קורס לפי מטרה', item: `${SITE_URL}/guides/by-goal` },
  ],
});

const faqSchema = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'איך יודעים איזה קורס מתאים למטרה שלי?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'קודם מגדירים מטרה ברורה: הסבה מקצועית, שיפור מיומנות, קידום בעבודה, עסק עצמאי, או למידה אישית. כל מטרה מוביל לסוג קורס שונה, משך שונה ותקציב שונה.',
      },
    },
    {
      '@type': 'Question',
      name: 'מה עדיף — ללמוד מהיר ולצאת לעבודה או ללמוד לעומק?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'תלוי במטרה. להסבה מהירה — בוטקאמפ אינטנסיבי. לעומק מקצועי — קורסים ממושכים יותר. לרוב המסבים מקצוע, עומק מינימלי שמאפשר לבצע עבודה אמיתית עדיף על ידע רחב שלא יישומי.',
      },
    },
    {
      '@type': 'Question',
      name: 'האם שווה ללמוד תחום שלא מתאים לאישיות שלי אם יש בו ביקוש גבוה?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'לרוב לא. ביקוש גבוה חשוב, אבל תחום שלא מתאים לאישיות יביא לנשירה בלימוד ולאי-שביעות רצון בעבודה. שווה למצוא תחום שמשלב ביקוש עם עניין אישי.',
      },
    },
  ],
});

const GOALS = [
  {
    id: 'career-change',
    title: 'הסבה מקצועית מלאה',
    description: 'רוצים לעבור לתחום חדש לחלוטין — מעצב לתכנות, ממשווק לדאטה, מכל מקצוע לטכנולוגיה.',
    recommendations: [
      'בוטקאמפ אינטנסיבי (4–6 חודשים, 40 שעות שבוע)',
      'עדיפות לפרונטלי — מבנה ומנטורינג חיוניים',
      'ודאו שיש הכנה לראיונות ולתיק עבודות',
      'בדקו אחוזי השמה ממשיים של בוגרים',
    ],
    budget: '14,000–22,000 ₪',
    duration: '4–6 חודשים',
    courses: [
      { label: 'קורסי תכנות', route: '/courses/programming' },
      { label: 'קורסי דאטה', route: '/courses/data-ai' },
      { label: 'קורסי סייבר', route: '/courses/cyber' },
    ],
  },
  {
    id: 'skill-upgrade',
    title: 'שיפור מיומנות קיימת',
    description: 'כבר עובדים בתחום ורוצים להוסיף מיומנות ספציפית: שפה חדשה, כלי, טכנולוגיה.',
    recommendations: [
      'קורס ממוקד (2–4 חודשים) על הנושא הספציפי',
      'אונליין עובד מצוין — גמישות ומחיר נמוך',
      'חפשו קורס עם תרגול אמיתי, לא רק הסברים',
      'Udemy, Coursera, קורסים ישראליים ממוקדים',
    ],
    budget: '300–6,000 ₪',
    duration: '1–4 חודשים',
    courses: [
      { label: 'קורסי AI', route: '/courses/ai' },
      { label: 'קורסי Web Dev', route: '/courses/web-dev' },
      { label: 'קורסי שיווק דיגיטלי', route: '/courses/digital-marketing' },
    ],
  },
  {
    id: 'promotion',
    title: 'קידום בעבודה',
    description: 'רוצים לעלות תפקיד, לעבור לניהול, או לקחת אחריות גדולה יותר במקום העבודה הנוכחי.',
    recommendations: [
      'זיהוי הפער הספציפי: מה חסר לתפקיד הבא?',
      'קורס ניהול / מנהיגות / מיומנויות ניהוליות',
      'אחרים: קורס טכנולוגי ספציפי לתפקיד המיועד',
      'שאלו את המנהל: מה יעזור לקידום?',
    ],
    budget: '2,000–10,000 ₪',
    duration: '2–6 חודשים',
    courses: [
      { label: 'קורסי שיווק דיגיטלי', route: '/courses/digital-marketing' },
      { label: 'קורסי AI', route: '/courses/ai' },
      { label: 'קורסי דאטה', route: '/courses/data-ai' },
    ],
  },
  {
    id: 'freelance',
    title: 'עסק עצמאי או פרילנס',
    description: 'רוצים לצאת לפרילנס, לפתוח עסק, או לספק שירותים ישירות ללקוחות.',
    recommendations: [
      'קורס שמלמד גם את הפרקטיקה העסקית, לא רק הטכנית',
      'בנייה מהירה של תיק עבודות שניתן להציג ללקוחות',
      'קורסי UI/UX, שיווק, תכנות — ממוקדים ומהירים',
      'חפשו קורסים עם קהילת בוגרים פעילה',
    ],
    budget: '3,000–12,000 ₪',
    duration: '2–5 חודשים',
    courses: [
      { label: 'קורסי UI/UX', route: '/courses/ui-ux' },
      { label: 'קורסי שיווק דיגיטלי', route: '/courses/digital-marketing' },
      { label: 'קורסי Web Dev', route: '/courses/web-dev' },
    ],
  },
  {
    id: 'personal',
    title: 'למידה אישית / תחביב',
    description: 'רוצים ללמוד תחום מעניין מסקרנות, ללא מטרה תעסוקתית מיידית.',
    recommendations: [
      'קורסים אונליין ועצמאיים — הכי גמישים ומשתלמים',
      'Coursera, edX, Udemy — מגוון רחב במחיר נמוך',
      'אין לחץ על לוחות זמנים — בקצב שלכם',
      'YouTube — הרבה תוכן חינמי ואיכותי',
    ],
    budget: '0–2,000 ₪',
    duration: 'בקצב האישי',
    courses: [
      { label: 'קורסי AI', route: '/courses/ai' },
      { label: 'קורסי שוק ההון', route: '/courses/stock-market' },
      { label: 'קורסי כושר', route: '/courses/fitness' },
    ],
  },
];

export default function ByGoalPage() {
  const router = useRouter();

  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <title>בחירת קורס לפי מטרה — הסבה, קידום, פרילנס, לימוד אישי | Kursi</title>
          <meta
            name="description"
            content="איזה קורס מתאים למטרה שלכם? מדריך לבחירת קורס לפי מטרה: הסבה מקצועית, שיפור מיומנות, קידום בעבודה, פרילנס, או למידה אישית — עם המלצות ספציפיות לכל מטרה."
          />
          <link rel="canonical" href={`${SITE_URL}/guides/by-goal`} />
          <meta property="og:url" content={`${SITE_URL}/guides/by-goal`} />
          <meta property="og:title" content="בחירת קורס לפי מטרה | Kursi" />
          <meta
            property="og:description"
            content="הסבה מקצועית, שיפור מיומנות, קידום, פרילנס — כל מטרה דורשת סוג קורס שונה. מדריך לבחירת נכון."
          />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={`${SITE_URL}/og-image.webp`} />
          <meta name="robots" content="index,follow" />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
        </Head>
      )}

      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <View style={styles.navBar}>
          <Link
            href="/courses"
            style={styles.navBtn}
            accessibilityRole="link"
          >
            <Text style={styles.navBtnText}>← קורסים</Text>
          </Link>
          <Link
            href="/"
            style={[styles.navBtn, styles.navBtnHome]}
            accessibilityRole="link"
          >
            <Text style={[styles.navBtnText, styles.navBtnHomeText]}>דף הבית</Text>
          </Link>
        </View>

        <View style={styles.innerContent}>
          <View style={styles.badge}>
            <Text style={styles.badgeText}>לפי מטרה</Text>
          </View>

          <Text
            style={styles.h1}
            {...(Platform.OS === 'web' && ({ role: 'heading', 'aria-level': 1 } as any))}
          >
            בחירת קורס לפי מטרה — כי לכל מטרה יש קורס אחר
          </Text>

          <Text style={styles.intro}>
            לפני שבוחרים תחום, בחרו מטרה. הסבה מקצועית דורשת קורס אחר מ"שיפור מיומנות".
            פרילנס דורש גישה שונה מ"קידום בעבודה". מדריך זה יעזור לכם למצוא את הסוג הנכון.
          </Text>

          {/* Goal cards */}
          {GOALS.map((goal, i) => (
            <View key={goal.id} style={styles.goalCard}>
              <Text style={styles.goalTitle}>{goal.title}</Text>
              <Text style={styles.goalDesc}>{goal.description}</Text>

              <View style={styles.metaRow}>
                <View style={styles.metaTag}>
                  <Text style={styles.metaLabel}>תקציב טיפוסי</Text>
                  <Text style={styles.metaValue}>{goal.budget}</Text>
                </View>
                <View style={styles.metaTag}>
                  <Text style={styles.metaLabel}>משך</Text>
                  <Text style={styles.metaValue}>{goal.duration}</Text>
                </View>
              </View>

              <Text style={styles.recommendationsTitle}>המלצות לבחירת קורס:</Text>
              <View style={styles.bulletList}>
                {goal.recommendations.map((rec, j) => (
                  <View key={j} style={styles.bulletRow}>
                    <Text style={styles.bulletDot}>•</Text>
                    <Text style={styles.bulletText}>{rec}</Text>
                  </View>
                ))}
              </View>

              <Text style={styles.coursesTitle}>קורסים רלוונטיים ב-Kursi:</Text>
              <View style={styles.courseLinks}>
                {goal.courses.map((course) => (
                  <Pressable
                    key={course.route}
                    style={({ pressed }) => [styles.courseLink, pressed && { opacity: 0.75 }]}
                    onPress={() => router.push(course.route as any)}
                    accessibilityRole="link"
                  >
                    <Text style={styles.courseLinkText}>{course.label} →</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ))}

          {/* FAQ */}
          <View style={styles.section}>
            <Text style={styles.h2}>שאלות נפוצות</Text>
            {[
              { q: 'איך יודעים איזה קורס מתאים למטרה שלי?', a: 'מגדירים מטרה ברורה קודם — הסבה, שיפור מיומנות, קידום, פרילנס, או למידה אישית. כל מטרה מוביל לסוג קורס שונה.' },
              { q: 'מה עדיף — מהיר ולצאת לעבודה, או לעומק?', a: 'להסבה מהירה — בוטקאמפ. לעומק מקצועי — קורסים ממושכים. לרוב, עומק מינימלי שמאפשר עבודה אמיתית עדיף על ידע רחב שלא יישומי.' },
              { q: 'האם כדאי ללמוד תחום עם ביקוש גבוה גם אם לא מתאים לאישיות?', a: 'לרוב לא. ביקוש חשוב, אבל תחום שלא מתאים מוביל לנשירה ולאי-שביעות רצון. שלבו ביקוש עם עניין אישי.' },
            ].map((item, i) => (
              <View key={i} style={styles.faqItem}>
                <Text style={styles.faqQ}>{item.q}</Text>
                <Text style={styles.faqA}>{item.a}</Text>
              </View>
            ))}
          </View>

          {/* CTA */}
          <View style={styles.ctaBlock}>
            <Text style={styles.ctaTitle}>לא בטוחים מה המטרה שלכם?</Text>
            <Text style={styles.ctaBody}>ה-AI של Kursi ישאל אתכם שאלות ויעזור להגדיר מטרה ולמצוא קורס מתאים.</Text>
            <Pressable
              style={({ pressed }) => [styles.ctaBtn, pressed && { opacity: 0.85 }]}
              onPress={() => router.push('/chat')}
              accessibilityRole="button"
            >
              <Text style={styles.ctaBtnText}>התחילו שיחה עם AI</Text>
            </Pressable>
          </View>

          {/* Internal links */}
          <View style={styles.linksSection}>
            <Text style={styles.linksTitle}>מדריכים נוספים</Text>
            <View style={styles.linksGrid}>
              {[
                { label: 'קורס אונליין vs פרונטלי', route: '/compare/online-vs-frontal' },
                { label: 'בחירת קורס לפי תקציב', route: '/guides/by-budget' },
                { label: 'בחירת קורס לפי עיר', route: '/guides/by-city' },
                { label: 'תכנות למתחילים', route: '/guides/beginners-programming' },
                { label: 'שאלות נפוצות', route: '/faq' },
              ].map((link) => (
                <Pressable
                  key={link.route}
                  style={({ pressed }) => [styles.linkItem, pressed && { opacity: 0.75 }]}
                  onPress={() => router.push(link.route as any)}
                  accessibilityRole="link"
                >
                  <Text style={styles.linkText}>{link.label} →</Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F2EBDD' },
  content: { paddingBottom: 56 },
  innerContent: { maxWidth: 820, alignSelf: 'center' as any, width: '100%', paddingHorizontal: 16 },
  navBar: {
    flexDirection: 'row' as any, justifyContent: 'space-between' as any,
    alignItems: 'center' as any, paddingHorizontal: 16, paddingVertical: 12, gap: 10,
  },
  navBtn: { paddingVertical: 8, paddingHorizontal: 14, borderRadius: 0, backgroundColor: '#FAF5EA' },
  navBtnHome: { backgroundColor: '#FAF5EA' },
  navBtnPressed: { opacity: 0.7 },
  navBtnText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary },
  navBtnHomeText: { color: '#7A6F5F' },
  badge: {
    backgroundColor: 'transparent', borderWidth: 1,
    borderColor: '#1A1611', borderRadius: 0,
    paddingHorizontal: 12, paddingVertical: 5, marginBottom: 10,
    alignSelf: 'flex-end' as any, marginTop: 16,
  },
  badgeText: { fontSize: 12, fontFamily: 'Heebo-Bold', color: theme.colors.primary },
  h1: {
    fontSize: Platform.OS === 'web' ? 32 : 24, fontFamily: 'Heebo-Bold', color: '#1A1611',
    textAlign: 'right' as any, marginBottom: 12, lineHeight: Platform.OS === 'web' ? 44 : 34,
  },
  intro: {
    fontSize: 16, fontFamily: 'Heebo-Regular', color: '#7A6F5F',
    textAlign: 'right' as any, lineHeight: 26, marginBottom: 20,
  },
  goalCard: {
    backgroundColor: '#FAF5EA', borderRadius: 0, borderWidth: 1,
    borderColor: '#1A1611', padding: 20, marginBottom: 14,
  },
  goalTitle: { fontSize: 20, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 8 },
  goalDesc: {
    fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A',
    textAlign: 'right' as any, lineHeight: 22, marginBottom: 14,
  },
  metaRow: { flexDirection: 'row-reverse' as any, gap: 10, marginBottom: 14 },
  metaTag: {
    flex: 1, backgroundColor: '#FAF5EA', borderRadius: 0,
    padding: 12, borderWidth: 1, borderColor: '#1A1611',
  },
  metaLabel: { fontSize: 11, fontFamily: 'Heebo-Regular', color: '#7A6F5F', textAlign: 'center' as any, marginBottom: 4 },
  metaValue: { fontSize: 14, fontFamily: 'Heebo-Bold', color: theme.colors.primary, textAlign: 'center' as any },
  recommendationsTitle: { fontSize: 14, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 8 },
  bulletList: { gap: 8, marginBottom: 14 },
  bulletRow: { flexDirection: 'row-reverse' as any, gap: 8, alignItems: 'flex-start' as any },
  bulletDot: { fontSize: 14, color: theme.colors.primary, fontFamily: 'Heebo-Bold', lineHeight: 22 },
  bulletText: { flex: 1, fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  coursesTitle: { fontSize: 14, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 8 },
  courseLinks: { flexDirection: 'row-reverse' as any, flexWrap: 'wrap' as any, gap: 8 },
  courseLink: {
    paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#FAF5EA',
    borderRadius: 0, borderWidth: 1, borderColor: '#1A1611',
  },
  courseLinkText: { fontSize: 13, fontFamily: 'Heebo-Medium', color: theme.colors.primary },
  section: {
    backgroundColor: '#FAF5EA', borderRadius: 0, borderWidth: 1,
    borderColor: '#1A1611', padding: 20, marginBottom: 14,
  },
  h2: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 14, lineHeight: 28 },
  faqItem: { marginBottom: 14 },
  faqQ: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 6 },
  faqA: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'right' as any, lineHeight: 22 },
  ctaBlock: { backgroundColor: '#FAF5EA', borderRadius: 0, padding: 24, alignItems: 'center' as any, marginTop: 8, marginBottom: 20 },
  ctaTitle: { fontSize: 18, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'center' as any, marginBottom: 8 },
  ctaBody: { fontSize: 14, fontFamily: 'Heebo-Regular', color: '#3A332A', textAlign: 'center' as any, marginBottom: 16, lineHeight: 22 },
  ctaBtn: { backgroundColor: theme.colors.primary, paddingVertical: 12, paddingHorizontal: 28, borderRadius: 0 },
  ctaBtnText: { fontSize: 15, fontFamily: 'Heebo-Bold', color: '#FAF5EA' },
  linksSection: { marginBottom: 16 },
  linksTitle: { fontSize: 17, fontFamily: 'Heebo-Bold', color: '#1A1611', textAlign: 'right' as any, marginBottom: 12 },
  linksGrid: { gap: 8 },
  linkItem: {
    paddingVertical: 12, paddingHorizontal: 16, backgroundColor: '#FAF5EA',
    borderRadius: 0, borderWidth: 1, borderColor: '#1A1611',
  },
  linkText: { fontSize: 14, fontFamily: 'Heebo-Medium', color: theme.colors.primary, textAlign: 'right' as any },
});
